import React, { useState } from "react";
import {
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Email,
  Close,
  PersonAdd
} from "@mui/icons-material";
import { url } from "../constants/url";
import { useThemeToggle } from "../context/ThemeContext";
import CustomAlert from "../components/customAlert"; // Import CustomAlert

const AddTeamMembers = ({ userData, companyId, onClose, open, onSuccess }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const [memberEmail, setMemberEmail] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message
    });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, severity: "success", message: "" });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!memberEmail.trim()) {
      showAlert("error", "Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      console.log("Adding member to company:", companyId, memberEmail);
      const response = await fetch(`${url}/api/admin/add-members-to-company`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: companyId,
          email: memberEmail,
        }),
      });

      const data = await response.json();
      console.log(data);
      
      if (response.ok) {
        showAlert("success", "Team member added successfully!");
        setMemberEmail("");
        
        // Call onSuccess prop if provided
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 500);
        }
        
        // Close modal after success
        setTimeout(() => {
          handleClose();
        }, 800);
      } else {
        showAlert("error", data.message || "Failed to add team member");
      }
    } catch (error) {
      console.error("❌ Error adding member:", error);
      showAlert("error", "Network error. Please try again.");
    }

    setLoading(false);
  };

  const handleClose = () => {
    setMemberEmail("");
    setLoading(false);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: 'none',
            bgcolor: 'white'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
            Add Team Member
          </Typography>
          <IconButton 
            onClick={handleClose}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                backgroundColor: 'rgba(0,0,0,0.04)',
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the email address to invite a team member to your company
          </Typography>

          <form onSubmit={handleAddMember}>
            <Stack spacing={3}>
              {/* Email Field with Grey Background */}
              <TextField
                fullWidth
                label="Email address"
                type="email"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                placeholder="team.member@example.com"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    '& fieldset': {
                      border: '1px solid #e0e0e0',
                    },
                    '&:hover fieldset': {
                      border: '1px solid #bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      border: '2px solid #1976d2',
                    },
                  }
                }}
                required
              />

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                {/* Red Cancel Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{
                    py: 1.2,
                    borderRadius: 1,
                    fontWeight: 600,
                    borderColor: '#f44336',
                    color: '#f44336',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      backgroundColor: 'rgba(244, 67, 54, 0.04)',
                      color: '#d32f2f',
                    },
                    '&:disabled': {
                      borderColor: 'grey.300',
                      color: 'grey.300',
                    }
                  }}
                >
                  Cancel
                </Button>
                
                {/* Green Add Button */}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading || !memberEmail.trim()}
                  startIcon={
                    loading ? 
                      <CircularProgress size={20} color="inherit" /> : 
                      <PersonAdd />
                  }
                  sx={{
                    py: 1.2,
                    borderRadius: 1,
                    backgroundColor: "#4caf50", // Green color
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: "#388e3c", // Darker green on hover
                    },
                    '&:disabled': {
                      backgroundColor: 'grey.300',
                    }
                  }}
                >
                  {loading ? "Adding..." : "Add Member"}
                </Button>
              </Stack>
            </Stack>
          </form>

          {/* Help Text */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
            >
              The member will receive an invitation to join your company
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default AddTeamMembers;